import { createContext, useContext } from 'react';
import { RouteV2Id } from 'routes/routesConfigV2';
import EnrollmentFlowPage, { EnrollmentFlowDomainParams } from 'store/flows/enrollment/enrollmentFlowPage';
import { FormInstanceType, FormStatuses } from 'types/formService';
import { getWebFormInstances } from 'api/operations/formService';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { requestApplyUserAction } from 'api/operations/enrollmentService';
import { USER_ACTIONS } from 'types/enrollmentService';
import { isEnrollmentPeriodNotEditable } from 'global/checklists-helpers/ChecklistHelper';

export default class EmployerFormsPageStore extends EnrollmentFlowPage {
  private _webFormInstances: FormInstanceType[] = [];

  public get webFormInstances(): FormInstanceType[] {
    return this._webFormInstances;
  }

  public get isEveryFormCompleted(): boolean {
    return this._webFormInstances.every((form) => form.status === FormStatuses.COMPLETE);
  }

  public get isEnrollmentPeriodNotEditable(): boolean {
    return isEnrollmentPeriodNotEditable(this._enrollmentPeriodModule.enrollmentPeriod.stage);
  }

  public async getInitialData(): Promise<void> {
    this.setIsInitialDataLoading(true);
    await this.getFlowInitialData();
    await runInAction(async () => {
      const instances = await getWebFormInstances(this._domainData.periodId);
      runInAction(() => {
        this._webFormInstances = instances.sort((a, b) => (a.form.formTitle > b.form.formTitle ? 1 : -1));
        this.setIsInitialDataLoading(false);
      });
    });
  }

  public async onComplete(): Promise<void> {
    if (this._domainData.periodId && this.isEveryFormCompleted) {
      this.setIsInitialDataLoading(true);
      await requestApplyUserAction(this._domainData.periodId, USER_ACTIONS.COMPLETE_WEB_FORMS);
      runInAction(() => {
        this.onCompleteRedirect();
      });
    }
  }

  constructor({ groupId, periodId }: EnrollmentFlowDomainParams) {
    super({ domainParams: { groupId, periodId }, nextPageId: RouteV2Id.ENROLLMENT_CHECKLIST });
    makeObservable<EmployerFormsPageStore, '_webFormInstances' | 'onComplete'>(this, {
      _webFormInstances: observable,
      webFormInstances: computed,
      isEveryFormCompleted: computed,
      isEnrollmentPeriodNotEditable: computed,
      getInitialData: action.bound,
      onComplete: action.bound,
    });
  }
}

const EmployerFormsPageStoreContext = createContext(new EmployerFormsPageStore({} as EnrollmentFlowDomainParams));

const useEmployerFormsPageStore = (): EmployerFormsPageStore => {
  return useContext(EmployerFormsPageStoreContext);
};

export { EmployerFormsPageStoreContext, useEmployerFormsPageStore };
